import { useQuery } from "@tanstack/react-query";
import { useGetList } from "ra-core";
import {
  Laptop,
  PhoneCall,
  Smartphone,
  TriangleAlert,
  Users,
} from "lucide-react";
import { Link } from "react-router";

import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import { getSupabaseClient } from "../../providers/supabase/supabase";
import type { GulaClient } from "../gulaTypes";
import { countByShift, type ShiftKey } from "../shifts";
import { useNow } from "../useNow";
import {
  agoLabel,
  durationLabel,
  emptySlotAlert,
  presenceLines,
  SLOTS,
  slotOfDate,
  type CallLogRow,
  type PresenceBlock,
  type PresenceLine,
  type PresenceRow,
  type PresenceState,
  type PresenceWorker,
} from "./presence";

const localDay = (date: Date) =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");

/** Presence, today's activity blocks and today's calls, every 30 s. */
const usePresenceData = () => {
  const { data: workers = [] } = useGetList<
    PresenceWorker & { no_work?: boolean }
  >("sales", {
    filter: { disabled: false },
    pagination: { page: 1, perPage: 50 },
    sort: { field: "id", order: "ASC" },
  });
  const { data } = useQuery({
    queryKey: ["gula-presence"],
    refetchInterval: 30_000,
    queryFn: async () => {
      const supabase = getSupabaseClient();
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      const [presence, blocks, calls] = await Promise.all([
        supabase
          .from("gula_presence")
          .select("sales_id,last_seen_at,last_active_at,calling_since,device"),
        supabase
          .from("gula_presence_blocks")
          .select("sales_id,block_at")
          .gte("block_at", start.toISOString())
          .limit(5000),
        supabase
          .from("gula_call_log")
          .select("sales_id,created_at,outcome")
          .eq("day", localDay(new Date()))
          .limit(5000),
      ]);
      for (const result of [presence, blocks, calls]) {
        if (result.error) throw result.error;
      }
      return {
        presence: (presence.data ?? []) as PresenceRow[],
        blocks: (blocks.data ?? []) as PresenceBlock[],
        calls: (calls.data ?? []) as CallLogRow[],
      };
    },
  });
  return {
    workers: workers.filter((worker) => !worker.no_work),
    presence: data?.presence ?? [],
    blocks: data?.blocks ?? [],
    calls: data?.calls ?? [],
    loaded: !!data,
  };
};

export const useTeamPresence = () => {
  const now = useNow(30_000);
  const { workers, presence, blocks, calls, loaded } = usePresenceData();
  const lines = presenceLines({ workers, presence, blocks, calls, now });
  return {
    lines,
    now,
    loaded,
    alert: loaded ? emptySlotAlert(lines, now) : null,
  };
};

const STATE: Record<
  PresenceState,
  { label: string; dot: string; text: string }
> = {
  calling: {
    label: "Llamando por WhatsApp",
    dot: "bg-lime-500 animate-pulse",
    text: "text-lime-700 dark:text-lime-300",
  },
  online: {
    label: "En línea",
    dot: "bg-lime-500",
    text: "text-lime-700 dark:text-lime-300",
  },
  idle: {
    label: "CRM abierto, sin actividad",
    dot: "bg-amber-400",
    text: "text-amber-700 dark:text-amber-300",
  },
  offline: {
    label: "Desconectado",
    dot: "bg-neutral-300 dark:bg-neutral-600",
    text: "text-muted-foreground",
  },
};

const stateDetail = (line: PresenceLine, now: number) => {
  if (line.state === "calling" && line.lastActiveAt)
    return `desde ${agoLabel(line.lastActiveAt, now)}`;
  if (line.state === "idle" && line.lastActiveAt)
    return `última actividad ${agoLabel(line.lastActiveAt, now)}`;
  if (line.state === "offline") {
    const last = line.lastActiveAt ?? line.lastSeenAt;
    return last
      ? `última actividad ${agoLabel(last, now)}`
      : "hoy sin actividad";
  }
  return null;
};

const PresenceRowItem = ({
  line,
  now,
  currentSlot,
}: {
  line: PresenceLine;
  now: number;
  currentSlot: string | null;
}) => {
  const state = STATE[line.state];
  const detail = stateDetail(line, now);
  const DeviceIcon =
    line.state === "calling"
      ? PhoneCall
      : line.device === "mobile"
        ? Smartphone
        : Laptop;
  return (
    <div className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:gap-4">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <span className={cn("h-3 w-3 shrink-0 rounded-full", state.dot)} />
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 font-medium">
            {line.worker.first_name} {line.worker.last_name}
            {line.worker.administrator ? (
              <span className="text-xs font-normal text-muted-foreground">
                (admin)
              </span>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-x-1.5 text-xs">
            <span
              className={cn(
                "inline-flex items-center gap-1 font-medium",
                state.text,
              )}
            >
              {line.state !== "offline" && line.device ? (
                <DeviceIcon className="h-3 w-3" />
              ) : null}
              {state.label}
            </span>
            {detail ? (
              <span className="text-muted-foreground">· {detail}</span>
            ) : null}
          </div>
          <div className="text-xs text-muted-foreground">
            Hoy: {line.callsToday} llamada{line.callsToday === 1 ? "" : "s"}
            {line.lastCallAt
              ? ` · última ${agoLabel(line.lastCallAt, now)}`
              : ""}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 pl-6 sm:pl-0">
        {SLOTS.map((slot) => {
          const work = line.slots[slot.key];
          const empty = !work.minutes && !work.calls;
          return (
            <div
              key={slot.key}
              className={cn(
                "min-w-[92px] rounded-xl border px-2.5 py-1 text-xs",
                slot.key === currentSlot &&
                  "border-lime-400 bg-lime-50 dark:bg-lime-950/30",
                empty && "text-muted-foreground",
              )}
              title={`${slot.label} (${slot.hours})`}
            >
              <div className="font-medium">{slot.label}</div>
              <div>
                {empty
                  ? "—"
                  : [
                      work.minutes ? durationLabel(work.minutes) : null,
                      `${work.calls} llam.`,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/** Red card on Inicio: a call slot is running and nobody is calling. */
export const EmptySlotBanner = ({
  presence,
  leadsWaiting,
}: {
  presence: ReturnType<typeof useTeamPresence>;
  /** Leads to call in the running slot */
  leadsWaiting?: number;
}) => {
  const { alert, now } = presence;
  if (!alert) return null;
  return (
    <div className="flex items-start gap-3 rounded-3xl border border-red-300 bg-red-50 p-4 text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-100">
      <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0" />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="font-semibold">
          Franja de {alert.slot.label.toLowerCase()} ({alert.slot.hours}): nadie
          llamando ahora
        </p>
        <p className="text-sm">
          {alert.nobodyThisSlot
            ? "Ningún setter se ha conectado en esta franja."
            : alert.last
              ? `El último en trabajar fue ${alert.last.name}, ${agoLabel(alert.last.at, now)}.`
              : "Hoy todavía no se ha conectado nadie."}
          {leadsWaiting
            ? ` Hay ${leadsWaiting} lead${leadsWaiting === 1 ? "" : "s"} para llamar en esta franja.`
            : ""}
        </p>
      </div>
      <Link
        to={`/clientes?turno=${alert.slot.key}`}
        className="shrink-0 rounded-full bg-red-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-800"
      >
        Ver leads
      </Link>
    </div>
  );
};

/** Inicio (admins): who is connected now and what each one did per slot. */
export const TeamPresenceCard = ({
  presence,
}: {
  presence: ReturnType<typeof useTeamPresence>;
}) => {
  const { lines, now, loaded } = presence;
  const working = lines.filter(
    (line) => line.state === "online" || line.state === "calling",
  ).length;
  const currentSlot = slotOfDate(new Date(now));
  return (
    <section className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <Users className="h-5 w-5 text-muted-foreground" />
        <h2 className="text-lg font-semibold">Setters ahora</h2>
        <span
          className={cn(
            "rounded-full px-2.5 py-0.5 text-xs font-medium",
            working
              ? "bg-lime-200 text-neutral-900"
              : "bg-muted text-muted-foreground",
          )}
        >
          {working ? `${working} trabajando` : "Nadie trabajando ahora"}
        </span>
      </div>
      <Card className="divide-y rounded-3xl px-4 py-1">
        {!loaded ? (
          <p className="py-4 text-sm text-muted-foreground">Cargando…</p>
        ) : lines.length ? (
          lines.map((line) => (
            <PresenceRowItem
              key={String(line.worker.id)}
              line={line}
              now={now}
              currentSlot={currentSlot}
            />
          ))
        ) : (
          <p className="py-4 text-sm text-muted-foreground">
            Hoy todavía no se ha conectado ningún setter.
          </p>
        )}
      </Card>
      <p className="px-1 text-xs text-muted-foreground">
        «En línea» = tiene el CRM abierto y lo está usando, o ha apuntado una
        llamada hace menos de 10 min. Las franjas suman el tiempo trabajado hoy.
      </p>
    </section>
  );
};

/** Inicio of the admins: warning when a slot has nobody calling + the team now. */
export const TeamNow = ({ clients }: { clients: GulaClient[] }) => {
  const presence = useTeamPresence();
  const leadsWaiting = presence.alert
    ? countByShift(clients)[presence.alert.slot.key as ShiftKey]
    : undefined;
  return (
    <>
      <EmptySlotBanner presence={presence} leadsWaiting={leadsWaiting} />
      <TeamPresenceCard presence={presence} />
    </>
  );
};
